import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  ExternalLink, 
  Download, 
  Calculator,
  CheckCircle2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AllProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = selectedCategory === "all" 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  const categories = ["all", ...new Set(products.map(p => p.category))];

  const handleDownloadPDF = async (productId, productName) => {
    try {
      const response = await axios.get(`${API}/products/${productId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${productName.replace(/\s+/g, '_')}_Spec_Sheet.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  return (
    <Layout>
      <div className="py-8 md:py-12" data-testid="all-products-page">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <Link 
                to="/" 
                className="inline-flex items-center text-[#00BCD4] hover:text-[#00acc1] font-medium mb-3"
                data-testid="back-to-home"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Home
              </Link>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-800 tracking-tight">
                All Products
              </h1>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    selectedCategory === cat
                      ? "bg-[#00BCD4] text-white"
                      : "bg-white border border-slate-200 text-slate-600 hover:border-[#00BCD4]"
                  }`}
                  data-testid={`filter-${cat}`}
                >
                  {cat === "all" ? "All" : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="empty-state">
              <p className="empty-state-title">No products found</p>
              <p className="empty-state-text">Try adjusting your filter or check back later.</p>
            </div>
          ) : (
            <div className="product-grid">
              {filteredProducts.map((product, index) => (
                <div 
                  key={product.id}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden card-hover animate-fadeIn"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`product-card-${product.id}`}
                >
                  {/* Card Header */}
                  <div className="p-6 border-b border-slate-100">
                    <span className="text-xs font-bold tracking-wider text-[#00BCD4] uppercase mb-2 block">
                      {product.category}
                    </span>
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">
                      {product.name}
                    </h3>
                    <p className="text-slate-600 text-sm line-clamp-2">
                      {product.description}
                    </p>
                  </div>

                  {/* Dosage */}
                  <div className="px-6 py-4 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs font-bold tracking-wider text-slate-400 uppercase">
                          Dosage
                        </span>
                        <p className="text-lg font-mono font-semibold text-slate-800">
                          {product.dosage_range}
                        </p>
                      </div>
                      <Link to="/calculator">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-[#00BCD4] hover:bg-[#000000]/10 hover:text-slate-800"
                        >
                          <Calculator className="w-4 h-4 mr-1" />
                          Calculate
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Applications */}
                  <div className="px-6 py-4">
                    <span className="text-xs font-bold tracking-wider text-slate-400 uppercase block mb-2">
                      Applications
                    </span>
                    <div className="flex flex-wrap">
                      {product.applications?.slice(0, 3).map((app, i) => (
                        <span key={i} className="app-tag">
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Features */}
                  <div className="px-6 py-4 border-t border-slate-100">
                    <span className="text-xs font-bold tracking-wider text-slate-400 uppercase block mb-3">
                      Key Features
                    </span>
                    <ul className="space-y-2">
                      {product.features?.slice(0, 3).map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Actions */}
                  <div className="px-6 py-4 bg-slate-50 flex gap-2">
                    <Link to={`/products/${product.id}`} className="flex-1">
                      <Button className="w-full bg-[#00BCD4] hover:bg-[#00acc1]">
                        View Details
                      </Button>
                    </Link>
                    {/* <Button 
                      variant="outline"
                      onClick={() => handleDownloadPDF(product.id, product.name)}
                      data-testid={`download-pdf-${product.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button> */}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AllProductsPage;
